import * as Sentry from '@sentry/react'
import merge from 'deepmerge'
import { reaction } from 'mobx'
import type { Instance, SnapshotOut } from 'mobx-state-tree'
import { types } from 'mobx-state-tree'

import type {
  CheckUserResponse,
  LoginResponse,
  LoginResponseError,
  SafeRequestResponse,
  SetupProgress,
  UpdateSponsorSetupProgressParams,
} from '@/services/api/api.types'
import { getDefaultSetupProgress, isSetupProgressEmpty } from '@/services/api/api.types'
import {
  getGrandfatheredProgress,
  type GrandfatheringData,
  shouldGrandfatherSponsor,
} from '@/services/setupProgressService'
import { withRootStore } from '@/services/state/helpers/withRootStore'
import { withSetProp } from '@/services/state/helpers/withSetProp'
import { getDefaultSponsor } from '@/utils/sponsors'
import * as storage from '@/utils/storage'

export const AuthenticationStoreModel = types
  .model('AuthenticationStore')
  .props({
    ready: types.optional(types.boolean, false),
    auth: types.maybe(types.frozen<LoginResponse['authenticationResult']>()),
    user: types.maybe(types.frozen<CheckUserResponse>()),
    setupProgress: types.optional(types.frozen<SetupProgress>(), getDefaultSetupProgress()),
    isInSetup: types.optional(types.boolean, false),
    setupStatusChecked: types.optional(types.boolean, false),
    pendingSponsorSelection: types.optional(types.boolean, false),
    availableSponsors: types.optional(types.frozen<LoginResponse['availableSponsors']>(), []),
  })
  .extend(withSetProp)
  .extend(withRootStore)
  .volatile<AuthenticationStoreVolatileProps>(() => ({
    refreshTokenTimer: undefined,
  }))
  .views(self => ({
    get isAuthenticated() {
      return !!self.auth?.idToken
    },
    get isSponsorAdmin() {
      return (self.user?.isSponsorAdmin || self.user?.isSponsor) ?? false
    },
  }))
  .actions(self => ({
    reset() {
      Sentry.setUser(null)
      self.auth = undefined
      self.user = undefined
      self.setupProgress = getDefaultSetupProgress()
      self.isInSetup = false
      self.setupStatusChecked = false
      self.pendingSponsorSelection = false
      self.availableSponsors = []
      self.rootStore.api.queryClient.clear()
      self.rootStore.api.setAuthToken(null)
      self.rootStore.configStore.setProp('serverBaseUrl', undefined)
      self.rootStore.configStore.setProp('analyticsServerBaseUrl', undefined)
      self.rootStore.api.setBaseUrl(undefined)
      storage.clearExceptPersistent()
      clearTimeout(self.refreshTokenTimer)
      self.refreshTokenTimer = undefined
    },
    /**
     * Sets the server URLs for API calls and configuration
     * @param serverUrl - The SME server URL
     * @param analyticsUrl - The analytics server URL (optional, defaults to serverUrl)
     */
    setServerUrls(serverUrl: string, analyticsUrl?: string) {
      self.rootStore.api.setBaseUrl(serverUrl)
      self.rootStore.configStore.setProp('serverBaseUrl', serverUrl)
      self.rootStore.configStore.setProp('analyticsServerBaseUrl', analyticsUrl || serverUrl)
    },
  }))
  .actions(self => ({
    async checkSetupStatus() {
      // Only check setup status for sponsor admins
      if (!self.isSponsorAdmin) {
        self.setProp('isInSetup', false)
        self.setProp('setupStatusChecked', true)
        return
      }

      // Check if setup is complete based on progress
      const progress = self.setupProgress
      const allStepsComplete = progress.createContent && progress.inviteUsers

      self.setProp('isInSetup', !allStepsComplete)

      self.setProp('setupStatusChecked', true)
    },
    async fetchSetupProgress() {
      if (!self.isSponsorAdmin) {
        self.setProp('setupProgress', getDefaultSetupProgress())
        return
      }

      try {
        const setupProgress = await this.getSetupProgress()
        self.setProp('setupProgress', setupProgress)
      } catch (error) {
        console.error('Failed to fetch setup progress:', error)
        self.setProp('setupProgress', getDefaultSetupProgress())
      }
    },
    async getSetupProgress(): Promise<SetupProgress> {
      if (!self.isSponsorAdmin) {
        return getDefaultSetupProgress()
      }

      try {
        const sponsorInfo = await self.rootStore.api.getSponsorInfo()
        const currentProgress = sponsorInfo?.setupProgress ?? getDefaultSetupProgress()

        // If we have existing setup progress with completed steps, return it
        if (!isSetupProgressEmpty(currentProgress)) {
          return currentProgress
        }

        // Check if this sponsor should be grandfathered in
        // (existing sponsor with no setup progress but has sponsor-specific content)
        const contentModules = await self.rootStore.api.getContentModules()
        const grandfatheringData: GrandfatheringData = { contentModules }

        if (shouldGrandfatherSponsor(grandfatheringData, currentProgress)) {
          const grandfatheredProgress = getGrandfatheredProgress()

          // Save the grandfathered progress to the backend
          try {
            if (self.user?.sponsorId) {
              const params: UpdateSponsorSetupProgressParams = {
                id: self.user.sponsorId,
                setup_progress: grandfatheredProgress,
              }
              await self.rootStore.api.updateSponsorSetupProgress(params)
            }
            return grandfatheredProgress
          } catch (saveError) {
            console.error('Failed to save grandfathered setup progress:', saveError)
            // Even if save fails, return the grandfathered progress for this session
            return grandfatheredProgress
          }
        }

        // New sponsor with no content - return default progress
        return getDefaultSetupProgress()
      } catch (error) {
        console.error('Failed to fetch setup progress:', error)
        return getDefaultSetupProgress()
      }
    },
    async logIn(params: {
      email: string
      password: string
    }): Promise<{ ok: boolean; message: string; code?: LoginResponseError['code'] }> {
      const response = await self.rootStore.api.safeRequest<typeof self.rootStore.api.login, LoginResponseError>(
        self.rootStore.api.login,
        {
          username: params.email,
          password: params.password,
        },
      )

      return this.processAuthResponse(response, true)
    },
    async refresh() {
      if (!self.auth?.refreshToken) {
        void self.rootStore.reset()
        self.setProp('ready', true)
        return
      }

      const response = await self.rootStore.api.safeRequest<typeof self.rootStore.api.refreshToken, LoginResponseError>(
        self.rootStore.api.refreshToken,
        { refresh_token: self.auth.refreshToken },
      )

      const processedResponse = await this.processAuthResponse(response, false)

      if (!processedResponse.ok) {
        void self.rootStore.reset()
      }

      // Mark as ready - setup status is checked during processAuthResponse
      self.setProp('ready', true)
    },
    async logOut() {
      await self.rootStore.api.safeRequest(self.rootStore.api.logout, {
        access_token: self.auth?.accessToken!,
        refresh_token: self.auth?.refreshToken!,
      })
      await self.rootStore.reset()
    },
    startRefreshTokenTimer() {
      if (!self.auth?.expiresIn) return

      self.refreshTokenTimer = setTimeout(() => void this.refresh(), self.auth.expiresIn * 900)
    },
    async processAuthResponse(
      response: SafeRequestResponse<LoginResponse, LoginResponseError>,
      isFreshLogin: boolean = false,
    ): Promise<{ ok: boolean; message: string; code?: LoginResponseError['code'] }> {
      if (response.ok) {
        if (response.data.authenticationResult?.idToken) {
          self.rootStore.api.setAuthToken(response.data.authenticationResult.idToken)

          // Store available sponsors if provided (for account menu display)
          if (response.data.availableSponsors && response.data.availableSponsors.length > 0) {
            self.setProp('availableSponsors', response.data.availableSponsors)
          }

          // Check if sponsor selection is required
          if (response.data.requiresSponsorSelection && response.data.availableSponsors) {
            if (response.data.availableSponsors.length === 0) {
              return { ok: false, message: 'No channels available' }
            }

            // Set sponsor selection state (MobX batches these synchronous updates)
            self.setProp('pendingSponsorSelection', true)
            self.setProp('auth', response.data.authenticationResult)

            // Use the default sponsor's environment for all API calls
            // This ensures we're calling the correct environment for the current user
            const defaultSponsor = getDefaultSponsor(response.data.availableSponsors)

            if (!defaultSponsor) {
              return {
                ok: false,
                message: 'No default channel available.',
              }
            }

            // Set the server URLs to the default sponsor's environment
            const serverUrl = defaultSponsor.targetUrls.smeServerUrl
            const analyticsUrl = defaultSponsor.targetUrls.smeServerUrl
            self.setServerUrls(serverUrl, analyticsUrl)

            // Reset sponsor context even when sponsor selection is required
            if (isFreshLogin) {
              try {
                const resetResponse = await self.rootStore.api.resetSponsorContext()
                console.log('Sponsor context reset (sponsor selection required):', resetResponse)

                // Small delay to allow browser to save the sponsor cookie (see C2-1801)
                // This prevents race conditions where subsequent API calls don't receive the cookie
                await new Promise(resolve => setTimeout(resolve, 100))
              } catch (error) {
                return {
                  ok: false,
                  message: `Failed to initialize sponsor context: ${error instanceof Error ? error.message : 'Unknown error'}`,
                }
              }
            }

            console.log('Sponsor selection state set:', {
              availableSponsors: self.availableSponsors?.length,
              pendingSponsorSelection: self.pendingSponsorSelection,
              isAuthenticated: self.isAuthenticated,
            })

            // Don't continue with normal flow - wait for sponsor selection
            return { ok: true, message: 'Sponsor selection required' }
          }

          // Set server URLs with environment variable override priority
          const envServerUrl = import.meta.env.VITE_SERVER_BASE_URL
          const envAnalyticsUrl = import.meta.env.VITE_ANALYTICS_SERVER_BASE_URL
          const serverUrl = envServerUrl || response.data.targetUrls?.smeServerUrl
          const analyticsUrl = envAnalyticsUrl || response.data.targetUrls?.smeServerUrl

          // Set server URLs - use new ones if available, otherwise use existing ones
          if (serverUrl) {
            self.setServerUrls(serverUrl, analyticsUrl)
          } else if (self.rootStore.configStore.serverBaseUrl) {
            // Use existing server URL for API calls during refresh
            self.rootStore.api.setBaseUrl(self.rootStore.configStore.serverBaseUrl)
          } else {
            // Only fail if we don't have server URLs at all
            return {
              ok: false,
              message: 'No server URL available. Please check your configuration.',
            }
          }

          // Reset sponsor context on the environment server with the login token
          // This must complete successfully before proceeding to other API calls
          // Only call reset during fresh login, not during token refresh
          if (isFreshLogin) {
            try {
              const resetResponse = await self.rootStore.api.resetSponsorContext()
              console.log('Sponsor context reset:', resetResponse)

              // Small delay to allow browser to save the sponsor cookie (see C2-1801)
              // This prevents race conditions where subsequent API calls don't receive the cookie
              await new Promise(resolve => setTimeout(resolve, 100))
            } catch (error) {
              // This is a critical step - if it fails, we should not proceed
              return {
                ok: false,
                message: `Failed to initialize sponsor context: ${error instanceof Error ? error.message : 'Unknown error'}`,
              }
            }
          }

          const userResponse = await self.rootStore.api.safeRequest(self.rootStore.api.checkUser)

          if (userResponse.ok) {
            Sentry.setUser({
              id: userResponse.data.userId,
              ...userResponse.data,
            })
          }

          if (!userResponse.ok || !userResponse.data.access.sme) {
            const message = !userResponse.ok && userResponse.data.message

            return {
              ok: false,
              message: message || 'You are not authorized to use this application.',
            }
          } else {
            self.setProp('user', userResponse.data)

            // Fetch setup progress and check setup status after user is set
            await this.fetchSetupProgress()
            await this.checkSetupStatus()

            // Notify server of successful login/authentication event
            try {
              await self.rootStore.api.notifyLogin({})
            } catch (error) {
              // Non-blocking: log error but don't prevent authentication completion
              console.warn('Failed to notify login event:', error)
            }

            // Mark as ready so routing can proceed
            self.setProp('ready', true)
          }

          self.setProp('auth', merge(self.auth ?? {}, response.data.authenticationResult)) // merging because refresh token response does not include new refresh token

          this.startRefreshTokenTimer()

          return { ok: true, message: 'Success' }
        } else {
          return { ok: false, message: 'Something went wrong.' }
        }
      } else {
        return {
          ok: false,
          message: response.data.message ?? response.error?.message ?? 'Something went wrong.',
          code: response.data.code,
        }
      }
    },
    async selectSponsor(
      sponsor: NonNullable<LoginResponse['availableSponsors']>[0],
    ): Promise<{ ok: boolean; message: string; error?: any; statusCode?: number }> {
      try {
        // Save original sponsor URLs to restore on failure
        const originalServerUrl = self.rootStore.configStore.serverBaseUrl
        const originalAnalyticsUrl = self.rootStore.configStore.analyticsServerBaseUrl

        // Set server URLs to the selected sponsor's environment
        const serverUrl = sponsor.targetUrls.smeServerUrl
        const analyticsUrl = sponsor.targetUrls.smeServerUrl

        if (serverUrl) {
          self.setServerUrls(serverUrl, analyticsUrl)
        }

        // Switch to the selected sponsor
        // This MUST complete before any other API calls are made
        let switchResponse
        try {
          switchResponse = await self.rootStore.api.switchSponsor(sponsor.sponsorId)
          console.log('Sponsor switch response:', switchResponse)

          // Small delay to allow browser to save the sponsor cookie (see C2-1801)
          // This prevents race conditions where subsequent API calls don't receive the cookie
          await new Promise(resolve => setTimeout(resolve, 100))
        } catch (error) {
          // Restore original sponsor URLs on failure
          if (originalServerUrl) {
            self.setServerUrls(originalServerUrl, originalAnalyticsUrl)
          }

          // Extract error details for debugging
          const e = error as any
          const statusCode = e.response?.status
          const serverError = e.response?.data

          // This is a critical step - if it fails, we should not proceed
          return {
            ok: false,
            message: `Failed to switch sponsor context: ${error instanceof Error ? error.message : 'Unknown error'}`,
            error: serverError,
            statusCode,
          }
        }

        // Ensure the switch completed successfully before proceeding
        if (!switchResponse) {
          // Restore original sponsor URLs on failure
          if (originalServerUrl) {
            self.setServerUrls(originalServerUrl, originalAnalyticsUrl)
          }

          return {
            ok: false,
            message: 'Sponsor switch did not return a valid response',
          }
        }

        // Clear all cached queries (content modules, metrics, etc.) to prevent showing stale data
        self.rootStore.api.queryClient.clear()

        // Continue with normal authentication flow
        const userResponse = await self.rootStore.api.safeRequest(self.rootStore.api.checkUser)

        if (userResponse.ok) {
          Sentry.setUser({
            id: userResponse.data.userId,
            ...userResponse.data,
          })
        }

        if (!userResponse.ok || !userResponse.data.access.sme) {
          return {
            ok: false,
            message: userResponse.data?.message || 'You are not authorized to use this application.',
          }
        } else {
          self.setProp('user', userResponse.data)

          // Broadcast so other tabs can show "channel changed, please reload" modal
          storage.broadcastChannelSwitch(userResponse.data.sponsorId)

          // Fetch setup progress and check setup status after user is set
          // These make remote calls which can be unreliable, so we handle failures gracefully
          try {
            await this.fetchSetupProgress()
            await this.checkSetupStatus()
          } catch (error) {
            console.error('Failed to fetch setup information:', error)
            // Continue with login even if setup info fails - use defaults
            // fetchSetupProgress already has fallback logic for defaults
          }

          // Clear sponsor selection state only after successful completion
          self.setProp('pendingSponsorSelection', false)

          // Notify server of successful sponsor switch event
          try {
            await self.rootStore.api.notifyLogin({})
          } catch (error) {
            // Non-blocking: log error but don't prevent sponsor switch completion
            console.warn('Failed to notify sponsor switch event:', error)
          }

          // Mark as ready so routing can proceed
          self.setProp('ready', true)
        }

        this.startRefreshTokenTimer()

        return { ok: true, message: 'Success' }
      } catch (error) {
        return {
          ok: false,
          message: `Error selecting sponsor: ${error instanceof Error ? error.message : 'Unknown error'}`,
        }
      }
    },
  }))
  .actions(self => ({
    async updateSetupProgress(progress: Partial<SetupProgress>): Promise<{ ok: boolean; message: string }> {
      if (!self.user?.sponsorId) {
        return { ok: false, message: 'User not found or missing sponsor ID' }
      }

      const currentProgress: SetupProgress = self.setupProgress
      const updatedProgress: SetupProgress = { ...currentProgress, ...progress }

      const params = { setup_progress: updatedProgress }

      try {
        await self.rootStore.api.updateSponsorSetupProgress(params)

        // Update the local setup progress
        self.setProp('setupProgress', updatedProgress)

        // Check if setup is now complete and update isInSetup status
        self.checkSetupStatus()

        return { ok: true, message: 'Setup progress updated successfully' }
      } catch (error) {
        return {
          ok: false,
          message: `Network error while updating setup progress: ${error instanceof Error ? error.message : 'Unknown error'}`,
        }
      }
    },

    // Developer functions for resetting setup progress
    // ------------------------------------------------------------
    async resetSetupProgress(): Promise<{ ok: boolean; message: string }> {
      if (!self.user?.sponsorId) {
        return { ok: false, message: 'User not found or missing sponsor ID' }
      }

      try {
        const emptyProgress = {} as SetupProgress
        const params = { setup_progress: emptyProgress }
        await self.rootStore.api.updateSponsorSetupProgress(params)

        // Update local state directly without triggering grandfathering check
        self.setProp('setupProgress', emptyProgress)
        self.setProp('isInSetup', true)

        return { ok: true, message: 'Setup progress reset successfully' }
      } catch (error) {
        return {
          ok: false,
          message: `Error resetting setup progress: ${error instanceof Error ? error.message : 'Unknown error'}`,
        }
      }
    },
  }))
  .actions(self => ({
    afterAttach() {
      reaction(
        () => self.rootStore.configStore.ready,
        ready => ready && void self.refresh(),
      )
    },
    beforeDestroy() {
      if (self.refreshTokenTimer) {
        clearTimeout(self.refreshTokenTimer)
        self.refreshTokenTimer = undefined
      }
    },
  }))

export interface AuthenticationStoreVolatileProps {
  refreshTokenTimer: NodeJS.Timeout | undefined
}
export interface AuthenticationStore extends Instance<typeof AuthenticationStoreModel> {}
export interface AuthenticationStoreSnapshot extends SnapshotOut<typeof AuthenticationStoreModel> {}
